import { c as _c } from "react/compiler-runtime";
import { RelatedSidebar, sidebarContentCardRenderer } from '@/components/Molecules/RelatedSidebar/RelatedSidebar';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentTranslation } from '@/components/Organisms/ContentTranslation/ContentTranslation';
import { MachineTranslationDisclaimer } from '@/components/Organisms/ContentTranslation/MachineTranslationDisclaimer';
import { RSFViewer } from '@/components/Organisms/RSFViewer/RSFViewer';
import { ArticlePageAside, ArticlePageMain, ArticlePageTemplate } from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { InfiniteScrollPage } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import type { IPageItemFetcher, IPageRendererProps } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import useTranslation from '@/hooks/useTranslation';
import { useUserContentViewTracker } from '@/hooks/useUserContentViewTracker';
import { useId } from 'react';
import { ContentType, type IContentItem, type IContentSearch, type IFormChoice, type IPerson } from '@/interfaces';
import { TrackedProductTypes } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { SearchContentType } from '@/services/Search';
import type { TranslationDetails } from 'bnef';
import { NewsAiSummary } from './NewsAiSummary';
import { useNewsActions } from './useNewsActions';
export interface INewsContentProps {
  newsStory: INewsStory;
  relatedStories: IContentItem[];
  translationLocales?: IFormChoice[];
}
export interface INewsStory extends IContentItem {
  body: string;
  byLine: string;
  abstract: string;
  originalLanguage: string;
  translationDetails?: TranslationDetails;
  byPeople?: IPerson[];
  canShare: boolean;
  aiSummary?: {
    text: string[];
  } | null;
}
const getAllNewsQuery = (query?: Record<string, any>): IContentSearch => {
  return {
    q: '',
    page: '1',
    pageSize: '60',
    contentType: SearchContentType.News,
    dateFrom: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    dateTo: new Date(Date.now()).toISOString(),
    ...query
  };
};
export const resultsBuilder = async (initialItem: IContentItem, query?: Record<string, any>): Promise<IContentItem[]> => {
  const resultItems: IContentItem[] = [];
  try {
    const searchResults = await new BrowserHttpClient().get({
      path: '/content_search',
      query: getAllNewsQuery(query)
    });
    if (searchResults?.items) {
      searchResults.items.forEach((item: IContentItem) => {
        if (item.id !== initialItem.id) {
          resultItems.push(item);
        }
      });
    }
    return resultItems;
  } catch (e) {
    console.warn('Failed to fetch from /content_search: ' + e.message);
    throw e;
  }
};
export const NewsPageAnalyticsInfo = {
  pageName: 'BNEF: News: Content View',
  siteSection: 'BNEF: News',
  type: TrackedProductTypes.News
};
export const newsRsfPageFetcher: IPageItemFetcher<INewsContentProps> = async (id: string) => {
  const pageData = await new BrowserHttpClient().get({
    path: '/news_stories/[ID]',
    replacements: {
      id: id.toLowerCase()
    }
  });
  if (pageData?.newsStory) {
    SailthruTracking.saveTags(id, pageData.newsStory.type, pageData.newsStory.tags);
    return {
      pageData: pageData as INewsContentProps
    };
  } else if (pageData?.canonicalResource) {
    return {
      canonicalResource: pageData.canonicalResource
    };
  }
  return null;
};
export const NewsRsfRenderer = t0 => {
  const $ = _c(43);
  const {
    pageData,
    isActive
  } = t0;
  useUserContentViewTracker(pageData.newsStory.id, ContentType.news, isActive);
  const {
    t
  } = useTranslation("common");
  const actions = useNewsActions(pageData.newsStory);
  const disclaimerId = useId();
  const t1 = pageData.newsStory.title;
  const t2 = pageData.newsStory.byLine;
  const t3 = pageData.newsStory.displayDate;
  let t4;
  if ($[0] !== t) {
    t4 = t("common:ContentType.news");
    $[0] = t;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t4) {
    t5 = [{
      label: t4,
      url: "/news"
    }];
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== actions || $[5] !== pageData.newsStory.byLine || $[6] !== pageData.newsStory.displayDate || $[7] !== pageData.newsStory.title || $[8] !== t5) {
    t6 = <ArticlePageHeader title={t1} bylineName={t2} date={t3} breadCrumbs={t5} actions={actions} />;
    $[4] = actions;
    $[5] = pageData.newsStory.byLine;
    $[6] = pageData.newsStory.displayDate;
    $[7] = pageData.newsStory.title;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = pageData.newsStory?.translationDetails ?? null;
  let t8;
  if ($[10] !== pageData.translationLocales) {
    t8 = pageData.translationLocales ?? [];
    $[10] = pageData.translationLocales;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== disclaimerId || $[13] !== pageData.newsStory.originalLanguage || $[14] !== pageData.newsStory.url || $[15] !== t7 || $[16] !== t8) {
    t9 = <ContentTranslation originalContentLanguage={pageData.newsStory.originalLanguage} currentTranslationDetails={t7} targetDestination={pageData.newsStory.url} languageOptions={t8} disclaimerId={disclaimerId} />;
    $[12] = disclaimerId;
    $[13] = pageData.newsStory.originalLanguage;
    $[14] = pageData.newsStory.url;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== pageData.newsStory.aiSummary || $[19] !== pageData.newsStory.translationDetails) {
    t10 = !pageData.newsStory?.translationDetails && <NewsAiSummary aiSummary={pageData.newsStory.aiSummary} />;
    $[18] = pageData.newsStory.aiSummary;
    $[19] = pageData.newsStory.translationDetails;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== pageData.newsStory.body) {
    t11 = <RSFViewer htmlContent={pageData.newsStory.body} />;
    $[21] = pageData.newsStory.body;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = pageData.newsStory?.translationDetails;
  let t13;
  if ($[23] !== disclaimerId || $[24] !== t12) {
    t13 = <MachineTranslationDisclaimer id={disclaimerId} translationDetails={t12} />;
    $[23] = disclaimerId;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t13 || $[29] !== t9) {
    t14 = <ArticlePageMain>{t9}{t10}{t11}{t13}</ArticlePageMain>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t13;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== pageData.newsStory.id || $[32] !== pageData.relatedStories || $[33] !== t) {
    t15 = pageData.relatedStories.length > 0 && {
      title: t("common:Sections.related"),
      items: pageData.relatedStories,
      itemRenderer: sidebarContentCardRenderer(`Related: Automated: News ${pageData.newsStory.id}`)
    };
    $[31] = pageData.newsStory.id;
    $[32] = pageData.relatedStories;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <ArticlePageAside><RelatedSidebar lists={[t15]} /></ArticlePageAside>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <ArticlePageTemplate>{t14}{t16}</ArticlePageTemplate>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17 || $[41] !== t6) {
    t18 = <>{t6}{t17}</>;
    $[40] = t17;
    $[41] = t6;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
};
export const NewsRsfPage = initialData => {
  const $ = _c(2);
  let t0;
  if ($[0] !== initialData) {
    t0 = <InfiniteScrollPage initialPageData={initialData} resultsBuilder={resultsBuilder} currentItem={initialData.newsStory} />;
    $[0] = initialData;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};